import { useOutletContext } from 'react-router-dom';
import { useMemo } from 'react';
import SummaryCards from "../components/SummaryCards";
import SpendingChart from "../components/SpendingChart";
import BudgetVsActualChart from "../components/BudgetVsActualChart";
import InsightsPanel from "../components/InsightsPanel";
import RecentTransactions from "../components/RecentTransactions";
import SavingsGoalsSummary from "../components/SavingsGoalsSummary";
import NetWorthSummary from "../components/NetWorthSummary";
import { useIsMobile } from "@/shared/hooks/useMediaQuery";
import { useSettingsContext } from '@/app/providers/SettingsProvider';
import { useCategoriesContext } from '@/app/providers/CategoriesProvider';
import { useModals } from '@/app/providers/ModalsProvider';
import { useHouseholdData } from '@/app/providers/HouseholdDataProvider';
import { usePeriod } from '@/app/providers/PeriodProvider';
import { useSpendingStreaks } from '../hooks/useSpendingStreaks';
import { useSmartInsights } from '../hooks/useSmartInsights';
import { useAnomalyDetection } from '../hooks/useAnomalyDetection';
import { useAchievements } from '../hooks/useAchievements';
import { computeAvgMonthlyContribution, forecastGoalCompletion } from '@/features/savings';
import { useSavings } from '@/features/savings';
import { useHouseholdContext } from '@/app/providers/HouseholdProvider';
import { buildSummaryRow } from '../utils/buildSummaryRow';
import { buildBudgetSuggestions } from '@/shared/utils/buildBudgetSuggestions';
import WhatIfCalculator from '../components/WhatIfCalculator';

export default function DashboardPage() {
  const mobile = useIsMobile();
  const { dashboard } = useOutletContext();
  const { t, currency } = useSettingsContext();
  const { incomeList, expenseMap, getGroup } = useCategoriesContext();
  const { tx } = useModals();
  const { data } = useHouseholdData();
  const { selMonth, selYear } = usePeriod();

  const { monthTxns, monthIncome, monthExpenses, monthNet, savingsRate, expByGroup, pieData, budgetVsActualData, insights } = dashboard;
  const { openAddTx, openEditTx, deleteTx, deleteTxId, setDeleteTxId } = tx;

  const allTxns = data ? (data.txns || []) : [];
  const currentBudgets = (data?.budgets || {})[selYear + '-' + (selMonth + 1)] || {};
  const { trendInsights, streakInsights } = useSpendingStreaks(allTxns, getGroup, incomeList);
  const { leafTrendInsights, paceInsights } = useSmartInsights({
    allTxns,
    monthTxns,
    selMonth,
    selYear,
    incomeList,
    expByGroup,
    currentBudgets,
    expenseMap,
  });

  const anomalyMap = useAnomalyDetection(allTxns, selMonth, selYear, incomeList);

  const budgetSuggestionInsights = useMemo(
    () => buildBudgetSuggestions(allTxns, currentBudgets, expenseMap, incomeList, selMonth, selYear),
    [allTxns, currentBudgets, expenseMap, incomeList, selMonth, selYear]
  );

  const achievementInsights = useAchievements({ allTxns, selMonth, selYear, incomeList, savingsRate });

  const { householdId } = useHouseholdContext();
  const { goals } = useSavings({ householdId, data });
  const today = new Date();

  const forecastInsights = useMemo(() => goals
    .filter(g => g.remaining > 0)
    .slice(0, 3)
    .flatMap(goal => {
      const avg = computeAvgMonthlyContribution(allTxns, goal.id, today);
      if (avg <= 0) return [];
      const { estimatedMonths, estimatedDate } = forecastGoalCompletion(goal, avg);
      if (!estimatedDate || estimatedMonths <= 0) return [];
      const dateLabel = estimatedDate.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
      return [{
        icon: '🔮',
        color: '#7c3aed',
        text: 'At your current savings pace, ' + String(goal.name) + ' is on track for ' +
          dateLabel + ' (' + currency + Math.round(avg).toLocaleString() + '/mo average)',
      }];
    })
    .slice(0, 1), [goals, allTxns, currency]);

  const anomalyInsights = useMemo(() => Array.from(anomalyMap.values())
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 3)
    .map(a => ({
      icon: a.type === 'high-amount' ? '🚨' : '🔍',
      color: a.type === 'high-amount' ? '#ef4444' : '#d97706',
      text: a.message,
    })), [anomalyMap]);

  const alerts = useMemo(
    () => [...paceInsights, ...anomalyInsights, ...(insights.alerts || [])],
    [paceInsights, anomalyInsights, insights]
  );
  const trends = useMemo(
    () => [...(insights.trends || []), ...trendInsights, ...streakInsights, ...leafTrendInsights, ...budgetSuggestionInsights, ...achievementInsights],
    [insights, trendInsights, streakInsights, leafTrendInsights, budgetSuggestionInsights, achievementInsights]
  );
  const insightGroups = useMemo(() => ({
    summary: buildSummaryRow(alerts, trends, forecastInsights),
    alerts,
    trends,
    forecasts: forecastInsights,
  }), [alerts, trends, forecastInsights]);

  return (
    <div>
      <SummaryCards
        monthIncome={monthIncome}
        monthExpenses={monthExpenses}
        monthNet={monthNet}
        savingsRate={savingsRate}
        theme={t}
        currency={currency}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
          gap: mobile ? 14 : 20,
          marginBottom: mobile ? 20 : 28,
        }}
      >
        <SpendingChart pieData={pieData} />
        <BudgetVsActualChart data={budgetVsActualData} />
      </div>
      <InsightsPanel insightGroups={insightGroups} theme={t} />
      <WhatIfCalculator
        allTxns={allTxns}
        selMonth={selMonth}
        selYear={selYear}
        incomeList={incomeList}
        expenseMap={expenseMap}
        goals={goals}
        currency={currency}
        theme={t}
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: mobile ? "1fr" : "1fr 1fr",
          gap: mobile ? 14 : 20,
          marginBottom: mobile ? 20 : 28,
        }}
      >
        <NetWorthSummary currency={currency} theme={t} />
        <SavingsGoalsSummary currency={currency} theme={t} />
      </div>
      <RecentTransactions
        monthTxns={monthTxns}
        openAddTx={openAddTx}
        openEditTx={openEditTx}
        deleteTx={deleteTx}
        deleteTxId={deleteTxId}
        setDeleteTxId={setDeleteTxId}
        incomeList={incomeList}
        getGroup={getGroup}
        currency={currency}
        theme={t}
        anomalyMap={anomalyMap}
      />
    </div>
  );
}
